import { Dynamic } from "solid-js/web";
import type { ComponentProps } from "solid-js";
import { cn } from "@cartero/shared";

/**
 * Cartero brand mark — an envelope resting on a warm postal stamp, rendered in
 * the signature vermilion→amber "sealed letter" gradient. It nods to classic
 * mail (envelope + stamp) while staying crisp and legible at any size. The
 * brand stops are constant OKLCH values so the mark reads identically in light
 * and dark. The gradient id is suffixed to stay unique when several logos share
 * a page (e.g. sidebar + auth).
 */
let markCounter = 0;

function LogoMark(props: ComponentProps<"svg">) {
  const gradId = `cartero-mark-${(markCounter += 1)}`;
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id={gradId} x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.6 0.19 34)" />
          <stop offset="1" stop-color="oklch(0.75 0.14 64)" />
        </linearGradient>
      </defs>
      {/* Postal stamp */}
      <rect x="3" y="4" width="26" height="24" rx="6" fill={`url(#${gradId})`} />
      {/* Envelope body */}
      <rect
        x="8"
        y="10.5"
        width="16"
        height="11"
        rx="1.8"
        fill="none"
        stroke="white"
        stroke-width="1.8"
      />
      {/* Envelope flap */}
      <path
        d="M8.6 11.4 16 16.6l7.4-5.2"
        stroke="white"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   */
  as?: "h1" | "p";
}

/** Editorial serif wordmark — the "letter" voice of Cartero. */
const WORDMARK_FONT = '"Fraunces", ui-serif, Georgia, serif';

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 shrink-0 drop-shadow-sm" />
          <span
            class="text-xl font-semibold tracking-tight text-foreground"
            style={{ "font-family": WORDMARK_FONT }}
          >
            Cartero
          </span>
        </>
      ) : (
        <>
          <LogoMark class="h-16 w-16 mb-2 shrink-0 drop-shadow-md transition-transform hover:scale-105 active:scale-95 duration-300" />
          <Dynamic
            component={wordmarkTag()}
            class="text-4xl font-semibold tracking-tight text-foreground"
            style={{ "font-family": WORDMARK_FONT }}
          >
            Cartero
          </Dynamic>
        </>
      )}
    </div>
  );
}
